import React, { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { Button, Typography } from "../../../../../../components";
import { billingService } from "../../../../../../api";
import { useUi } from "../../../../../../context/ui.context";
import Dialog from '../../../../../../components/dialog';
import CreateServiceModal from '../services/create-service-modal';
import Skeleton from 'react-loading-skeleton';
import { useTheme } from "styled-components";
import ChangePricingPlanModal from './change-pricing-plan-modal';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 0px 50px 0px;
  box-sizing: border-box;
  position: relative;
`;

const ServiceCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background: ${({ theme }) => theme.palette[10]};
  border: 1px solid ${({ theme }) => theme.palette[200]};
  border-radius: 8px;
`;

const DetailRow = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.palette[850]};
`;

const DetailLabel = styled.span`
  font-weight: 500;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const CurrencyWarning = styled.p`
  margin: 8px 0 0 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette[850]};
  opacity: 0.7;
`;

const OpenstackService: FC = () => {
  const theme: any = useTheme();
  const { userId } = useParams();
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [services, setServices] = useState<any[] | null>(null);
  const [projects, setProjects] = useState<Record<number, any>>({});
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [changePlanModal, setChangePlanModal] = useState<{ open: boolean; service: any }>({ open: false, service: null });
  const hasServiceWithProject = services?.some(
    (s: any) => s.status === 'active' || s.status === 'suspended'
  ) || false;

  const fetchData = () => {
    if (!userId) return;
    setLoading(true);
    billingService.getOpenstackServices(userId)
      .then((res: any) => {
        setServices(res.data.services || []);
        setProjects(res.data.projects || {});
      })
      .catch((e: any) => {
        console.error('Error fetching openstack services:', e);
        setServices([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const handleTerminate = (service: any) => {
    if (!window.confirm(t("TERMINATE_SERVICE_CONFIRM"))) return;
    billingService.terminateService(service.id)
      .then(() => {
        viewAlert({
          severity: 'info',
          message: t("SERVICE_TERMINATION_SCHEDULED"),
        });
        fetchData();
      })
      .catch((e: any) => {
        viewAlert({
          severity: 'error',
          message: e?.response?.data?.detail || t("ERROR_OCCURRED"),
        });
      });
  };

  if (loading) {
    return (
      <Container>
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width={200}
          height={24}
          style={{ marginBottom: '16px' }}
        />
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          height={120}
          style={{ borderRadius: '8px' }}
        />
      </Container>
    );
  }

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Typography variant="h2">
          {t("OPENSTACK_SERVICE")}
        </Typography>
      </div>

      {services && services.length === 0 && (
        <Typography variant="body" customStyles={{ opacity: '0.5' }}>
          {t("NO_OPENSTACK_SERVICE_FOR_CLIENT")}
        </Typography>
      )}

      {services && services.map((service: any) => {
        const project = projects[service.id];
        const isTerminated = service.status === 'terminated';
        const isPending = service.status === 'pending';

        return (
          <ServiceCard key={service.id}>
            <DetailRow>
              <DetailLabel>{t("SERVICE_STATUS")}:</DetailLabel> {service.status}
            </DetailRow>
            <DetailRow>
              <DetailLabel>{t("PRODUCT_NAME")}:</DetailLabel> {service.product?.name}
            </DetailRow>
            {service.cycle && (
              <DetailRow>
                <DetailLabel>{t("PRODUCT_CYCLE")}:</DetailLabel> {service.cycle.display_name}
              </DetailRow>
            )}
            {service.free_of_charge && (
              <DetailRow>
                <DetailLabel>{t("IS_FREE")}:</DetailLabel> {t("YES")}
                {service.is_free
                  ? ` (${t("SERVICE_MARKED_AS_FREE")})`
                  : ` (${t("PRODUCT_IS_FREE")})`
                }
              </DetailRow>
            )}
            {service.pricing_plan && (
              <DetailRow>
                <DetailLabel>{t("PRICING_PLAN")}:</DetailLabel> {service.pricing_plan.name}
              </DetailRow>
            )}
            <DetailRow>
              <DetailLabel>{t("SERVICE")}:</DetailLabel>{' '}
              <Link to={`/billing/services/${service.id}`} style={{ textDecoration: 'underline' }}>
                {service.id}
              </Link>
            </DetailRow>

            {!isTerminated && project && (
              <>
                {project.project_id && project.name && (
                  <DetailRow>
                    <DetailLabel>{t("PROJECT")}:</DetailLabel>{' '}
                    <Link to={`/openstack/projects/${project.id}`} style={{ textDecoration: 'underline' }}>
                      {project.name}
                    </Link>
                  </DetailRow>
                )}
                {project.project_domain_id && (
                  <DetailRow>
                    <DetailLabel>{t("PROJECT_DOMAIN_ID")}:</DetailLabel> {project.project_domain_id}
                  </DetailRow>
                )}
              </>
            )}

            {!isTerminated && !isPending && (
              <>
                <CurrencyWarning>
                  {t("CURRENCY_CONVERSION_WARNING")}
                </CurrencyWarning>
                <ActionsRow>
                  <Button
                    variant="stroke"
                    size="small"
                    title={t("CHANGE_PRICING_PLAN")}
                    onClick={() => setChangePlanModal({ open: true, service })}
                  />
                  <Button
                    variant="stroke"
                    size="small"
                    title={t("TERMINATE_SERVICE")}
                    customStyles="color: #E85048; border-color: #E85048;"
                    onClick={() => handleTerminate(service)}
                  />
                </ActionsRow>
              </>
            )}
          </ServiceCard>
        );
      })}

      {!hasServiceWithProject && (
        <div style={{ marginTop: '16px' }}>
          <Button
            variant="secondary"
            size="small"
            title={t("CREATE_OPENSTACK_SERVICE")}
            onClick={() => setCreateModalOpen(true)}
          />
        </div>
      )}

      {createModalOpen && (
        <Dialog noIframe open={createModalOpen} handleClose={() => setCreateModalOpen(false)}>
          <CreateServiceModal
            clientId={userId!}
            onCancel={() => setCreateModalOpen(false)}
            onSuccess={() => {
              setCreateModalOpen(false);
              fetchData();
            }}
          />
        </Dialog>
      )}

      {changePlanModal.open && changePlanModal.service && (
        <Dialog noIframe open={changePlanModal.open} handleClose={() => setChangePlanModal({ open: false, service: null })}>
          <ChangePricingPlanModal
            service={changePlanModal.service}
            onCancel={() => setChangePlanModal({ open: false, service: null })}
            onSuccess={() => {
              setChangePlanModal({ open: false, service: null });
              fetchData();
            }}
          />
        </Dialog>
      )}
    </Container>
  );
};

export default OpenstackService;
